import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatIDR, formatDateTime, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Receipt, Ticket as TicketIcon, ArrowRight, ArrowClockwise, CreditCard, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const METHOD = { midtrans: "Midtrans", stripe: "Stripe", ipaymu: "iPaymu" };
const STATUS = {
  paid: { label: "Lunas", cls: "bg-[#00FF66]/15 text-[#00FF66]" },
  pending: { label: "Menunggu Pembayaran", cls: "bg-yellow-400/15 text-yellow-400" },
  failed: { label: "Gagal", cls: "bg-secondary/15 text-secondary" },
  expired: { label: "Kedaluwarsa", cls: "bg-zinc-600/30 text-zinc-400" },
  cancelled: { label: "Dibatalkan", cls: "bg-zinc-600/30 text-zinc-400" },
};

export default function OrderHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/orders/my/history").then((r) => setOrders(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const checkStatus = async (o) => {
    setBusy(o.id);
    try {
      if (o.provider === "midtrans") await api.get(`/payments/midtrans/status/${o.id}`);
      else if (o.provider === "ipaymu") await api.get(`/payments/ipaymu/status/${o.id}`);
      await load();
      toast.success("Status pembayaran diperbarui");
    } catch (e) { toast.error("Gagal memeriksa status"); }
    setBusy(null);
  };

  const cancelOrder = async (o) => {
    setBusy(o.id);
    try { await api.post(`/orders/${o.id}/cancel`); toast.success("Pesanan dibatalkan"); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membatalkan"); }
    setBusy(null);
  };

  const changeMethod = async (o) => {
    setBusy(o.id);
    try { await api.post(`/orders/${o.id}/cancel`); } catch (e) { /* ignore */ }
    setBusy(null);
    navigate(`/events/${o.event_id}`);
  };

  return (
    <div className="min-h-screen bg-background grain">
      <Navbar />
      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        <h1 className="font-heading text-4xl font-black tracking-tight mb-2">Riwayat Transaksi</h1>
        <p className="text-zinc-500 mb-8">Semua pesanan dan status pembayaran Anda.</p>

        {loading ? (
          <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 bg-card border border-white/10 animate-pulse" />)}</div>
        ) : orders.length === 0 ? (
          <div className="text-center py-20 bg-card border border-white/10">
            <Receipt size={48} className="text-zinc-700 mx-auto mb-4" />
            <p className="text-zinc-400 mb-6">Belum ada transaksi.</p>
            <Link to="/"><Button className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none">Jelajahi Acara</Button></Link>
          </div>
        ) : (
          <div className="space-y-3" data-testid="orders-history-list">
            {orders.map((o) => {
              const st = STATUS[o.status] || { label: o.status, cls: "bg-white/10 text-zinc-300" };
              return (
                <div key={o.id} data-testid={`order-row-${o.id}`} className="bg-card border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${st.cls}`}>{st.label}</span>
                      <span className="text-[10px] uppercase tracking-wider bg-primary/15 text-primary px-2 py-0.5">{METHOD[o.provider] || "-"}</span>
                    </div>
                    <h3 className="font-heading font-bold text-lg text-white truncate">{o.event_name}</h3>
                    <p className="text-xs text-zinc-500 mt-1">
                      {o.ticket_count} tiket · {formatDateTime(o.created_at)}
                    </p>
                    <p className="text-[10px] text-zinc-600 font-mono mt-0.5">#{o.id.slice(0, 18)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-bold text-primary text-xl">{formatIDR(o.total)}</p>
                    {o.status === "paid" && (
                      <Link to="/tickets" data-testid={`order-view-tickets-${o.id}`}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-white hover:text-primary mt-1">
                        <TicketIcon size={14} /> Lihat Tiket <ArrowRight size={12} />
                      </Link>
                    )}
                    {o.status === "pending" && (
                      <div className="flex flex-wrap gap-2 justify-end mt-2">
                        <Button data-testid={`check-status-${o.id}`} onClick={() => checkStatus(o)} disabled={busy === o.id}
                          size="sm" className="h-8 bg-primary text-black font-bold hover:bg-cyan-300 rounded-none gap-1">
                          <ArrowClockwise size={14} weight="bold" /> Cek Status
                        </Button>
                        <Button data-testid={`change-method-${o.id}`} onClick={() => changeMethod(o)} disabled={busy === o.id}
                          size="sm" variant="outline" className="h-8 border-white/20 rounded-none gap-1 hover:bg-white hover:text-black">
                          <CreditCard size={14} weight="bold" /> Ubah Metode
                        </Button>
                        <Button data-testid={`cancel-order-${o.id}`} onClick={() => cancelOrder(o)} disabled={busy === o.id}
                          size="sm" variant="outline" className="h-8 border-white/20 rounded-none gap-1 hover:bg-secondary hover:text-white hover:border-secondary">
                          <XCircle size={14} weight="bold" /> Batalkan
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
